<script>
import Spinner from 'shared/components/Spinner.vue';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NotificationRow from './NotificationRow.vue';

export default {
  components: {
    Spinner,
    NextButton,
    NotificationRow,
  },
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
    onClickNotification: {
      type: Function,
      default: () => {},
    },
    onMarkAllDoneClick: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      // read_at flips to true moments after load (readAll fires as soon as
      // the page opens), which would erase the unread/read split before the
      // user ever sees it. Snapshotting "was this unread when I first saw
      // it" on arrival keeps the New/Earlier grouping stable for the rest
      // of this page view regardless of that background mutation.
      unreadSnapshot: {},
    };
  },
  computed: {
    ...mapGetters({
      notificationMetadata: 'notifications/getMeta',
    }),
    showEmptyResult() {
      return !this.isLoading && this.notifications.length === 0;
    },
    // Split into two explicit groups instead of scanning for a read/unread
    // transition in list order -- that approach rendered nothing at all
    // once every item on the page had already been marked read, leaving no
    // "new" section and no divider to anchor it above the history rows.
    unreadNotifications() {
      return this.notifications.filter(item => this.isUnread(item));
    },
    readNotifications() {
      return this.notifications.filter(item => !this.isUnread(item));
    },
  },
  watch: {
    notifications: {
      immediate: true,
      handler(list) {
        list.forEach(item => {
          if (!(item.id in this.unreadSnapshot)) {
            this.unreadSnapshot[item.id] = !item.read_at;
          }
        });
      },
    },
  },
  methods: {
    isUnread(notification) {
      return this.unreadSnapshot[notification.id] ?? !notification.read_at;
    },
  },
};
</script>

<template>
  <section class="flex-grow flex-shrink w-full h-full px-6 py-8 overflow-hidden">
    <div class="flex w-full items-center justify-between gap-2 mb-4">
      <h6 class="text-xl font-medium text-n-slate-12">
        {{ $t('NOTIFICATIONS_PAGE.HEADER') }}
      </h6>
      <NextButton
        v-if="notificationMetadata.unreadCount"
        type="submit"
        sm
        :label="$t('NOTIFICATIONS_PAGE.MARK_ALL_DONE')"
        :is-loading="isUpdating"
        @click="onMarkAllDoneClick"
      />
    </div>
    <table class="notifications-table w-full overflow-auto">
      <tbody v-show="!isLoading">
        <tr v-if="unreadNotifications.length === 0" class="notification--divider-row">
          <td colspan="5">
            <p class="notification--empty-new">
              {{ $t('NOTIFICATIONS_PAGE.LIST.NO_NEW') }}
            </p>
          </td>
        </tr>
        <NotificationRow
          v-for="notificationItem in unreadNotifications"
          :key="notificationItem.id"
          :notification="notificationItem"
          :is-unread="true"
          :on-click-notification="onClickNotification"
        />
        <tr v-if="readNotifications.length" class="notification--divider-row">
          <td colspan="5">
            <div class="notification--divider">
              <span>{{ $t('NOTIFICATIONS_PAGE.LIST.EARLIER') }}</span>
            </div>
          </td>
        </tr>
        <NotificationRow
          v-for="notificationItem in readNotifications"
          :key="notificationItem.id"
          :notification="notificationItem"
          :is-unread="false"
          :on-click-notification="onClickNotification"
        />
      </tbody>
    </table>
    <div
      v-if="showEmptyResult"
      class="flex flex-col items-center justify-center py-16 px-1"
    >
      <div
        class="flex items-center justify-center mb-4 rounded-full size-14 bg-n-alpha-2"
      >
        <span class="text-n-slate-11 i-lucide-bell size-6" />
      </div>
      <h3 class="w-full mb-1 text-xl font-medium text-center text-n-slate-12">
        {{ $t('NOTIFICATIONS_PAGE.LIST.404') }}
      </h3>
      <p class="w-full max-w-[24rem] mx-auto text-center text-n-slate-11">
        {{ $t('NOTIFICATIONS_PAGE.LIST.EMPTY_SUBTITLE') }}
      </p>
    </div>
    <div v-if="isLoading" class="notifications--loader">
      <Spinner />
      <span>{{ $t('NOTIFICATIONS_PAGE.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.notifications-table {
  > tbody {
    > tr {
      @apply cursor-pointer;

      &:hover {
        @apply bg-n-slate-3;
      }

      &.is-active {
        @apply bg-n-slate-4 dark:bg-n-slate-6;
      }

      > td {
        &.conversation-count-item {
          @apply pl-6 rtl:pl-0 rtl:pr-6;
        }
      }

      &:last-child {
        @apply border-b-0;
      }
    }
  }
}

.notifications--loader {
  @apply text-base flex items-center justify-center p-10;
}

.notification--divider-row {
  @apply cursor-default;

  &:hover {
    @apply bg-transparent;
  }
}

.notification--divider {
  @apply flex items-center gap-3 pt-4 pb-1.5 text-xs font-medium text-n-slate-10;

  &::after {
    content: '';
    @apply flex-grow h-px bg-n-weak;
  }
}

.notification--empty-new {
  @apply py-4 text-sm text-n-slate-11;
}
</style>
